/**
 * <ia-anchor-nav>, the sticky in-page navigation (sections/ia-anchor-nav).
 *
 * The links are Liquid (_ia-anchor-link blocks). This resolves each link's
 * data-anchor to an element, marks the link of the section in view, and turns
 * a click into a scroll that lands the target under the bar. A target is
 * named four ways, tried in this order: a section's "Section name" setting
 * ("Sweepstakes" matches its data-section-name, case-insensitive), an element
 * id ("#redeem"), a section key from the template ("sweeps" matches
 * [id$="__sweeps"]), or a CSS selector. A named or keyed target resolves to
 * the section wrapper, so the jump lands on the section's own top edge.
 * The bar's height goes out as --ia-anchors-height, the slot the
 * PDP anchors bar also fills, so accordions and other scroll-into-view code
 * clear it through --ia-sticky-offset.
 *
 * The header hides itself on scroll down, so a jump asks it to hide and hold
 * (EVENTS.headerHide) and offsets by the bar alone, the way the PDP sticky
 * bar does; the hold is released once the scroll has gone quiet.
 */
import { EVENTS, publish } from 'ia-util-pubsub';

// No cross-browser "smooth scroll finished" event yet: Safari lacks scrollend.
const SCROLL_IDLE_MS = 150;
// The section whose top has passed this share of the viewport is the one in view.
const PROBE = 0.4;

class IaAnchorNav extends HTMLElement {
  connectedCallback() {
    this.links = [...this.querySelectorAll('[data-anchor]')];
    this.targets = this.links
      .map((link) => ({ link, el: this.resolve(link.dataset.anchor) }))
      .filter((entry) => entry.el);
    // A plain #hash landing (no JS scroll) still clears the pinned chrome.
    this.targets.forEach(({ el }) => {
      el.style.scrollMarginTop = 'calc(var(--ia-sticky-offset, 0px) + 16px)';
    });

    this.onClick = (event) => this.handleClick(event);
    this.onScroll = () => this.queueUpdate();
    this.onResize = () => {
      this.measure();
      this.queueUpdate();
    };
    this.addEventListener('click', this.onClick);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    this.measure();
    this.update();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    cancelAnimationFrame(this.frame);
    this.scrollEndController?.abort();
    clearTimeout(this.scrollIdleTimer);
    document.documentElement.style.setProperty('--ia-anchors-height', '0px');
  }

  resolve(name) {
    const key = (name || '').trim();
    if (!key) return null;
    const wanted = key.toLowerCase();
    const named = [...document.querySelectorAll('[data-section-name]')].find(
      (el) => el.dataset.sectionName.trim().toLowerCase() === wanted
    );
    if (named) return named.closest('.shopify-section') || named;
    if (key.startsWith('#')) return document.getElementById(key.slice(1));
    if (/^[\w-]+$/.test(key)) return document.querySelector(`[id$="__${key}"]`);
    try {
      return document.querySelector(key);
    } catch {
      return null;
    }
  }

  measure() {
    document.documentElement.style.setProperty('--ia-anchors-height', `${this.offsetHeight}px`);
  }

  queueUpdate() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.update();
    });
  }

  update() {
    if (!this.targets.length) return;
    const probe = window.innerHeight * PROBE;
    let active = null;
    this.targets.forEach((entry) => {
      if (entry.el.getBoundingClientRect().top <= probe) active = entry;
    });
    this.targets.forEach((entry) => {
      const on = entry === active;
      entry.link.classList.toggle('is-active', on);
      if (on) {
        entry.link.setAttribute('aria-current', 'location');
      } else {
        entry.link.removeAttribute('aria-current');
      }
    });
    if (active) this.reveal(active.link);
  }

  /** Keeps the active link inside the row where the row scrolls (mobile). */
  reveal(link) {
    const list = link.closest('[data-anchor-list]');
    if (!list || list.scrollWidth <= list.clientWidth) return;
    const item = link.getBoundingClientRect();
    const row = list.getBoundingClientRect();
    if (item.left >= row.left && item.right <= row.right) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    list.scrollTo({
      left: list.scrollLeft + (item.left + item.width / 2) - (row.left + row.width / 2),
      behavior: reduce ? 'auto' : 'smooth',
    });
  }

  handleClick(event) {
    const link = event.target.closest('[data-anchor]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    const entry = this.targets.find((candidate) => candidate.link === link);
    if (!entry) return;
    event.preventDefault();
    this.jump(entry.el);
  }

  jump(el) {
    publish(EVENTS.headerHide, { detail: { locked: true } });
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const top = el.getBoundingClientRect().top + window.scrollY - this.offsetHeight;
    window.scrollTo({ top: Math.max(top, 0), behavior: reduce ? 'auto' : 'smooth' });
    this.releaseHeaderAfterScroll();
  }

  /* Release on the native scrollend where it exists, otherwise once scrolling
     has gone quiet, which also covers a page that never moved. */
  releaseHeaderAfterScroll() {
    this.scrollEndController?.abort();
    this.scrollEndController = new AbortController();
    const { signal } = this.scrollEndController;

    const release = () => {
      clearTimeout(this.scrollIdleTimer);
      this.scrollEndController?.abort();
      this.scrollEndController = null;
      publish(EVENTS.headerHide, { detail: { locked: false } });
    };
    const restartIdle = () => {
      clearTimeout(this.scrollIdleTimer);
      this.scrollIdleTimer = setTimeout(release, SCROLL_IDLE_MS);
    };

    window.addEventListener('scrollend', release, { signal });
    window.addEventListener('scroll', restartIdle, { signal, passive: true });
    restartIdle();
  }
}

if (!customElements.get('ia-anchor-nav')) {
  customElements.define('ia-anchor-nav', IaAnchorNav);
}
